import React, { useState, useEffect, useContext } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { LanguageContext } from '../contexts/LanguageContext'

const HeaderContainer = styled.header`
    position: relative;
    top: 0;
    left: 0;
    right: 0;
    padding: 0;
    z-index: 1000;
    background-color: var(--background);
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`

const NavContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 70px;
    padding: 0 1.5rem;
    max-width: 1200px;
    margin: 0 auto;
`

const Logo = styled.a`
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--text);
    text-decoration: none;
    transition: all 0.3s ease;

    span.one {
        color: var(--text-light);
        transition: color 0.3s ease;
    }

    span.cal {
        color: var(--primary);
    }

    span.ai {
        color: var(--text-light);
        transition: color 0.3s ease;
    }

    &:hover {
        span.one,
        span.ai {
            color: var(--primary);
        }
    }
`

const Controls = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
`

const LanguageToggle = styled.button`
    background: none;
    border: none;
    color: var(--text);
    cursor: pointer;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    padding: 0.5rem;
    border-radius: 8px;
    font-weight: 600;
    transition: all 0.3s ease;

    &:hover {
        background-color: var(--light);
    }
`

const LanguageMenu = styled.div`
    position: absolute;
    top: 100%;
    right: 0;
    background-color: var(--card-bg);
    border-radius: 8px;
    box-shadow: var(--shadow);
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    min-width: 120px;
    overflow: hidden;
    z-index: 100;
    border: 1px solid var(--border-color);

    button {
        background: none;
        border: none;
        padding: 0.8rem 1rem;
        text-align: left;
        cursor: pointer;
        transition: background-color 0.3s ease;
        color: var(--text);

        &:hover {
            background-color: var(--light);
        }

        &.active {
            font-weight: 600;
            color: var(--primary);
        }
    }
`

const Header = () => {
    const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false)
    const { language, setAppLanguage } = useContext(LanguageContext)
    const { i18n } = useTranslation()

    const languages = [
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'Français' },
        { code: 'es', name: 'Español' },
        { code: 'de', name: 'Deutsch' },
        { code: 'ru', name: 'Русский' }
    ]

    useEffect(() => {
        const saved = localStorage.getItem('language')
        const lang = saved || 'en'
        i18n.changeLanguage(lang)
        setAppLanguage(lang)
        if (!saved) localStorage.setItem('language', 'en')
    }, [i18n, setAppLanguage])

    const toggleLanguageMenu = () => {
        setIsLanguageMenuOpen(open => !open)
    }

    const changeLanguage = code => {
        i18n.changeLanguage(code)
        setAppLanguage(code)
        localStorage.setItem('language', code)
        setIsLanguageMenuOpen(false)
    }

    return (
        <HeaderContainer>
            <NavContainer>
                <Logo href="/">
                    <span className="one">1</span>
                    <span className="cal">cal</span>
                    <span className="ai">.ai</span>
                </Logo>
                <Controls>
                    <LanguageToggle onClick={toggleLanguageMenu}>
                        {language.toUpperCase()}
                        <svg
                            style={{
                                marginLeft: '5px',
                                transform: isLanguageMenuOpen ? 'rotate(180deg)' : 'rotate(0)',
                                transition: 'transform 0.3s ease'
                            }}
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                    </LanguageToggle>
                    <LanguageMenu isOpen={isLanguageMenuOpen}>
                        {languages.map(l => (
                            <button key={l.code} onClick={() => changeLanguage(l.code)} className={language === l.code ? 'active' : ''}>
                                {l.name}
                            </button>
                        ))}
                    </LanguageMenu>
                </Controls>
            </NavContainer>
        </HeaderContainer>
    )
}

export default Header
